import React from "react";
import Box from "@mui/material/Box";

interface LicensePlateIconProps {
  style?: React.CSSProperties;
  "aria-hidden"?: boolean;
}

/**
 * A CSS-based license plate icon component.
 * Renders a generic license plate shape with provincial/territory styling.
 */
function LicensePlateIcon({ style, ...props }: LicensePlateIconProps) {
  const rawSize = style?.fontSize ?? "80px";
  // Ensure size has px unit for CSS calc()
  const baseSize = typeof rawSize === "number" ? `${rawSize}px` : rawSize;

  const iconColor = style?.color;
  const size = `calc(${baseSize} * 2.1)`;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: `calc(${size} * 0.6)`,
        // backgroundColor: "#f5f5f5",
        border: (theme) =>
          `calc(${size} * 0.01) solid ${iconColor ?? theme.palette.primary.dark}`,
        borderRadius: `calc(${size} * 0.08)`,
        position: "relative",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        overflow: "hidden",
      }}
      {...props}
    >
      {/* Bolt holes - top left */}
      <Box
        sx={{
          position: "absolute",
          top: "8%",
          left: "5%",
          width: `calc(${size} * 0.04)`,
          height: `calc(${size} * 0.04)`,
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.25)",
          boxShadow: "inset 1px 1px 2px rgba(0,0,0,0.4)",
        }}
      />
      {/* Bolt holes - top right */}
      <Box
        sx={{
          position: "absolute",
          top: "8%",
          right: "5%",
          width: `calc(${size} * 0.04)`,
          height: `calc(${size} * 0.04)`,
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.25)",
          boxShadow: "inset 1px 1px 2px rgba(0,0,0,0.4)",
        }}
      />
      {/* Bolt holes - bottom left */}
      <Box
        sx={{
          position: "absolute",
          bottom: "8%",
          left: "5%",
          width: `calc(${size} * 0.04)`,
          height: `calc(${size} * 0.04)`,
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.25)",
          boxShadow: "inset 1px 1px 2px rgba(0,0,0,0.4)",
        }}
      />
      {/* Bolt holes - bottom right */}
      <Box
        sx={{
          position: "absolute",
          bottom: "8%",
          right: "5%",
          width: `calc(${size} * 0.04)`,
          height: `calc(${size} * 0.04)`,
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.25)",
          boxShadow: "inset 1px 1px 2px rgba(0,0,0,0.4)",
        }}
      />
      {/* Plate number */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Courier New', monospace",
          fontWeight: 700,
          fontSize: `calc(${size} * 0.20)`,
          color: (theme) => iconColor ?? theme.palette.primary.dark,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
          // 3D embossed effect
          textShadow: `
            0 1px 1px rgba(255,255,255,0.8),
            0 -1px 1px rgba(0,0,0,0.3),
            1px 0 1px rgba(0,0,0,0.2),
            -1px 0 1px rgba(0,0,0,0.2),
            0 2px 2px rgba(0,0,0,0.15)
          `,
        }}
      >
        YYC·123
      </Box>
    </Box>
  );
}

export default LicensePlateIcon;
